import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { reduceContainerWidth, scaleFromSnapshot, type ContainerScaleSnapshot } from './containerScale';

export type ContainerScaleResult = {
  ref: RefObject<HTMLDivElement | null>;
  scale: number;
  measuredWidth: number;
};

const INITIAL_SNAPSHOT: ContainerScaleSnapshot = { measuredWidth: 0, latchedBelow: false };

/**
 * Observes a container element and reports a CSS scale factor so a fixed target
 * width (e.g. A4 ≈ 793px) fits the available width. Never scales above 1.
 *
 * Width updates within a scrollbar of the last sample are ignored, and the
 * fit/scale decision is latched (`containerScale.ts`) so a scrollbar cannot
 * swap PageRender's DOM on every observation.
 */
export function useContainerScale(targetPx: number): ContainerScaleResult {
  const ref = useRef<HTMLDivElement | null>(null);
  const [snapshot, setSnapshot] = useState<ContainerScaleSnapshot>(INITIAL_SNAPSHOT);
  const snapshotRef = useRef(snapshot);
  const rafIdRef = useRef<number | null>(null);

  const scheduleUpdate = useCallback(
    (width: number) => {
      const apply = (nextWidth: number) => {
        const next = reduceContainerWidth(snapshotRef.current, nextWidth, targetPx);
        if (next === snapshotRef.current) return;

        snapshotRef.current = next;
        setSnapshot(next);
      };

      if (typeof requestAnimationFrame !== 'function') {
        apply(width);
        return;
      }
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(() => {
        apply(width);
        rafIdRef.current = null;
      });
    },
    [targetPx]
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    // jsdom / older environments may lack ResizeObserver — stay at scale 1.
    if (typeof ResizeObserver === 'undefined') {
      scheduleUpdate(el.getBoundingClientRect().width || targetPx);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      scheduleUpdate(entry.contentRect.width);
    });

    observer.observe(el);
    scheduleUpdate(el.getBoundingClientRect().width);

    return () => {
      observer.disconnect();
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [scheduleUpdate, targetPx]);

  const scale = scaleFromSnapshot(snapshot, targetPx);

  return { ref, scale, measuredWidth: snapshot.measuredWidth };
}
